/**
 * QRCodeService - Handles QR code generation and parsing for address sharing
 */

import QRCode from 'qrcode';

/**
 * Data format for address sharing QR codes
 */
const QR_DATA_VERSION = '1.0';

/**
 * Generates a QR code data URL for an address and name
 * @param {string} address - Wallet address
 * @param {string} name - Display name for the address
 * @returns {Promise<string>} - Data URL for the QR code image
 */
export const generateAddressQR = async (address, name = '') => {
  try {
    const qrData = {
      version: QR_DATA_VERSION,
      type: 'address',
      address: address,
      name: name || `Wallet ${address.slice(0, 6)}...${address.slice(-4)}`,
      timestamp: Date.now()
    };

    const qrString = JSON.stringify(qrData);

    // Generate QR code as data URL
    const qrDataUrl = await QRCode.toDataURL(qrString, {
      errorCorrectionLevel: 'M',
      width: 256,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    });

    return qrDataUrl;
  } catch (error) {
    console.error('Error generating QR code:', error);
    throw new Error('Failed to generate QR code');
  }
};

/**
 * Generates a shareable URL for adding an address
 * @param {string} address - Wallet address
 * @param {string} name - Display name for the address
 * @param {string} baseUrl - Base URL for the application (default: current origin)
 * @returns {string} - Shareable URL
 */
export const generateShareableURL = (address, name = '', baseUrl = window.location.origin) => {
  const params = new URLSearchParams();
  params.append('address', address);

  if (name && name.trim()) {
    params.append('name', name.trim());
  }

  return `${baseUrl}/address_add?${params.toString()}`;
};

/**
 * Parses QR code data to extract address information
 * @param {string} qrString - Raw QR code string data
 * @returns {Object|null} - Parsed address data or null if invalid
 */
export const parseAddressQR = (qrString) => {
  try {
    const data = JSON.parse(qrString);

    // Validate required fields
    if (!data.address || !data.type || data.type !== 'address') {
      return null;
    }

    // Basic address validation (should be 42 chars starting with 0x)
    if (!/^0x[a-fA-F0-9]{40}$/.test(data.address)) {
      return null;
    }

    return {
      address: data.address,
      name: data.name || `Wallet ${data.address.slice(0, 6)}...${data.address.slice(-4)}`,
      version: data.version,
      timestamp: data.timestamp
    };
  } catch (error) {
    // Try parsing as plain address (fallback for simple QR codes)
    if (/^0x[a-fA-F0-9]{40}$/.test(qrString.trim())) {
      const address = qrString.trim();
      return {
        address: address,
        name: `Wallet ${address.slice(0, 6)}...${address.slice(-4)}`,
        version: 'legacy',
        timestamp: Date.now()
      };
    }

    return null;
  }
};

/**
 * Copies text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} - Success status
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
};

/**
 * Downloads a data URL as a file
 * @param {string} dataUrl - Data URL to download
 * @param {string} filename - Filename for the download
 */
export const downloadDataUrl = (dataUrl, filename = 'qr-code.png') => {
  try {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Failed to download QR code:', error);
  }
};

export default {
  generateAddressQR,
  generateShareableURL,
  parseAddressQR,
  copyToClipboard,
  downloadDataUrl
};